import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppInput from '@nxsys/components/AppInput';
import AppSelect from '@nxsys/components/AppSelect';
import { StyledAppSelect } from '@nxsys/components/AppSelect/index.styled';
import { irelandCountyOptions } from '@nxsys/constants/AppConstants';
import { countryOptions } from '@nxsys/constants/countries';
import { checkPost } from '@nxsys/utils';
import { Form } from 'antd';
import React from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import StepCard from './StepCard';
import {
  formatPostCode,
  validatePostCode,
} from '@nxsys/utils/formFieldValidation';
import { AppState } from '@nxsys/store';
import { IRELAND } from '@nxsys/store/constants/keys';

interface AddAddressDetailProps {
  stepNumber: number;
  address1?: string | null;
  address2?: string | null;
  address3?: string | null;
  city?: string | null;
  county?: string | null;
  country?: string | null;
  postCode?: string | null;
  completed?: boolean;
  onSubmitStep: (values: any) => void;
}

type AddAddressDetailValues = {
  address1: string;
  address2: string;
  address3: string;
  city: string;
  county: string;
  country: string;
  postCode: string;
};

const AddAddressDetail: React.FC<AddAddressDetailProps> = ({
  stepNumber,
  address1,
  address2,
  address3,
  city,
  county,
  country,
  postCode,
  completed = false,
  onSubmitStep,
}) => {
  const [form] = Form.useForm<AddAddressDetailValues>();
  const { messages } = useIntl();
  const { payrollCountry, isBureau } = useSelector(
    (state: AppState) => state.appStore,
  );
  const isIreland = payrollCountry === IRELAND;

  const onFinish = (values: AddAddressDetailValues) => {
    const params = {
      addressDetails: {
        address: [values?.address1, values?.address2, values?.address3],
        city: values?.city || null,
        county:
          typeof values?.county === 'object'
            ? (values?.county as any)?.value || null
            : values?.county || null,
        country: values?.country || null,
        postCode: values?.postCode || null,
      },
    };
    onSubmitStep(params);
  };

  return (
    <StepCard
      title={messages['prospect.onboarding.step.addAddressDetails'] as string}
      stepNumber={stepNumber}
      completed={completed}
      actions={[
        {
          variant: 'outlined',
          name: messages['common.save'] as string,
          onClick: () => form.submit(),
        },
      ]}
    >
      <AppFormContainer
        form={form}
        onFinish={onFinish}
        initialValues={{
          address1,
          address2,
          address3,
          city,
          county,
          country,
          postCode,
        }}
        wrapperCol={{ span: 24 }}
        layout="vertical"
      >
        <Form.Item
          label={messages['assignment.address.address1'] as string}
          name="address1"
          rules={[
            {
              required: true,
              message: messages['validation.nameRequired'] as string,
            },
          ]}
        >
          <AppInput
            placeholder={messages['assignment.address.address1'] as string}
          />
        </Form.Item>

        <Form.Item
          label={messages['assignment.address.address2'] as string}
          name="address2"
          rules={[
            {
              required: true,
              message: messages['validation.nameRequired'] as string,
            },
          ]}
        >
          <AppInput
            placeholder={messages['assignment.address.address2'] as string}
          />
        </Form.Item>

        <Form.Item
          label={messages['assignment.address.address3'] as string}
          name="address3"
        >
          <AppInput
            placeholder={messages['assignment.address.address3'] as string}
          />
        </Form.Item>

        <Form.Item
          label={messages['assignment.address.city'] as string}
          name="city"
        >
          <AppInput
            placeholder={messages['assignment.address.enterCity'] as string}
          />
        </Form.Item>

        <Form.Item
          label={messages['assignment.address.county'] as string}
          name="county"
        >
          {isIreland ? (
            <AppSelect
              showSearch
              options={irelandCountyOptions}
              placeholder={
                messages['assignment.address.selectCounty'] as string
              }
            />
          ) : (
            <AppInput
              placeholder={
                messages['assignment.address.selectCounty'] as string
              }
            />
          )}
        </Form.Item>

        <Form.Item
          label={messages['assignment.address.country'] as string}
          name="country"
        >
          <StyledAppSelect
            showSearch
            options={countryOptions}
            placeholder={messages['assignment.address.selectCountry'] as string}
            allowClear
          />
        </Form.Item>

        {/* NX-5228: the rest of this form already branches on country — the
            county field is a picker of Irish counties and the validator below
            uses the Irish format — but the label stayed on the UK message key.
            Set inline rather than through getFieldLabelByCountry: that helper
            returns "EIR Code", which is also what the employee export headers
            are built from, and those headers are matched on re-import. */}
        <Form.Item
          label={
            isIreland
              ? 'Eircode'
              : (messages['assignment.address.postCode'] as string)
          }
          name="postCode"
          rules={[
            {
              required: true,
              message: messages['form.pleaseEnter'] as string,
            },
            {
              validator: async (_, value) => {
                if (!value) return;
                const country = form.getFieldValue('country');
                if (!isBureau) {
                  if (!checkPost(value, country)) {
                    throw new Error(messages['form.invalidPostcode'] as string);
                  }
                } else {
                  if (!validatePostCode(value, payrollCountry)) {
                    throw new Error(
                      (isIreland
                        ? messages['form.invalidEirCode']
                        : messages['form.invalidPostcode']) as string,
                    );
                  }
                }
              },
            },
          ]}
        >
          <AppInput
            placeholder={messages['assignment.address.enterPostCode'] as string}
            upperCaseAll
            onChange={(e) => {
              const countryValue = form.getFieldValue('country');
              form.setFieldValue(
                'postCode',
                formatPostCode(e.target.value, countryValue),
              );
            }}
            maxLength={8}
          />
        </Form.Item>
      </AppFormContainer>
    </StepCard>
  );
};

export default AddAddressDetail;
